import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { formatCurrency, privatize } from '@/lib/utils/revenueUtils'
import type { LandingPageRow } from './hooks/useAttributionData'

import type { GoalType } from './AttributionCampaigns'

type LandingPageGoals = Record<string, { trials: number; form_submissions: number }>

interface LandingPagesTableProps {
  landingPages: LandingPageRow[]
  searchTerm: string
  connectorType: 'stripe' | 'foxy' | null
  privacyMode?: boolean
  goal?: GoalType
  landingPageGoals?: LandingPageGoals
}

export default function LandingPagesTable({ landingPages, searchTerm, connectorType, privacyMode = false, goal = 'revenue', landingPageGoals = {} }: LandingPagesTableProps) {
  return (
    <div className="responsive-table-wrapper">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 hover:bg-transparent">
            <TableHead className="data-table-th min-w-[280px]">Page</TableHead>
            <TableHead className="data-table-th text-right min-w-[100px]">
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1 justify-end cursor-help">Entry visitors</TooltipTrigger>
                <TooltipContent>Number of unique visitors whose first page was this one</TooltipContent>
              </Tooltip>
            </TableHead>
            {goal === 'revenue' && <>
              <TableHead className="data-table-th text-right min-w-[120px]">
                <Tooltip><TooltipTrigger className="flex items-center justify-end cursor-help">Attributed revenue</TooltipTrigger><TooltipContent className="max-w-xs">Revenue from {connectorType || 'payment'} transactions linked to sessions that started on this page</TooltipContent></Tooltip>
              </TableHead>
              <TableHead className="data-table-th text-right min-w-[80px]">
                <Tooltip><TooltipTrigger className="flex items-center gap-1 justify-end cursor-help">Conv. Rate</TooltipTrigger><TooltipContent>Percentage of entry visitors that resulted in a transaction</TooltipContent></Tooltip>
              </TableHead>
              <TableHead className="data-table-th text-right min-w-[100px]">
                <Tooltip><TooltipTrigger className="flex items-center gap-1 justify-end cursor-help">Rev/Visitor</TooltipTrigger><TooltipContent>Average attributed revenue per entry visitor</TooltipContent></Tooltip>
              </TableHead>
            </>}
            {goal === 'trials' && <TableHead className="data-table-th text-right min-w-[80px]">Trials</TableHead>}
            {goal === 'forms' && <TableHead className="data-table-th text-right min-w-[80px]">Form submits</TableHead>}
            {goal === 'visits' && <TableHead className="data-table-th text-right min-w-[80px]">Entry share</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {landingPages.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center py-12 text-muted-foreground/70">
                {searchTerm ? 'No pages match your search' : 'No landing page data available for this period.'}
              </TableCell>
            </TableRow>
          ) : (
            landingPages
              .filter((p) => !searchTerm || p.page?.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((page, idx) => {
                const revDollars = page.revenueCents / 100
                const revPerVisit = page.visits > 0 ? revDollars / page.visits : 0
                const hasConversion = revDollars > 0
                const convRate = hasConversion && page.visits > 0 ? (1 / page.visits * 100) : 0
                return (
                  <TableRow key={idx} className="data-table-row border-b border-border hover:bg-muted/50">
                    <TableCell className="data-table-cell data-table-cell-primary">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block truncate max-w-[350px] cursor-help">{page.page || '/'}</span>
                        </TooltipTrigger>
                        <TooltipContent>{page.page || '/'}</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell className="data-table-cell data-table-cell-numeric">{formatNumeric(page.visits)}</TableCell>
                    {goal === 'revenue' && <>
                      <TableCell className="data-table-cell data-table-cell-numeric">
                        {revDollars > 0 ? <span className="font-medium text-foreground">{privatize(formatCurrency(revDollars), privacyMode)}</span> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="data-table-cell data-table-cell-numeric text-xs">
                        {convRate > 0 ? <span className="font-medium text-foreground">{privatize(`${convRate.toFixed(1)}%`, privacyMode)}</span> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="data-table-cell data-table-cell-numeric">
                        {revPerVisit > 0 ? privatize(`$${revPerVisit.toFixed(2)}`, privacyMode) : '—'}
                      </TableCell>
                    </>}
                    {goal === 'trials' && <TableCell className="data-table-cell data-table-cell-numeric">
                      {(() => { const n = landingPageGoals[page.page]?.trials ?? 0; return n > 0 ? <span className="font-medium">{n}</span> : <span className="text-muted-foreground">—</span> })()}
                    </TableCell>}
                    {goal === 'forms' && <TableCell className="data-table-cell data-table-cell-numeric">
                      {(() => { const n = landingPageGoals[page.page]?.form_submissions ?? 0; return n > 0 ? <span className="font-medium">{n}</span> : <span className="text-muted-foreground">—</span> })()}
                    </TableCell>}
                    {goal === 'visits' && <TableCell className="data-table-cell data-table-cell-numeric text-xs text-muted-foreground">—</TableCell>}
                  </TableRow>
                )
              })
          )}
        </TableBody>
      </Table>
    </div>
  )
}
